import { CNS_API_BASE } from "@/config";
import { getBrowserIanaTimeZone } from "@/lib/browser-timezone";
import {
  CNSWorkspaceAnalytics,
  CNSWorkspace,
  CNSCreateWorkspace,
  CNSPlanTemplateSummary,
  CNSWorkspaceBillingProfileAssignment,
} from "@/models/cns";
import { throwOnProblemResponse } from "@/types/problem-detail";

const WORKSPACES_URL = `${CNS_API_BASE}/workspaces`;
const WORKSPACES_CURRENT_USER_URL = `${CNS_API_BASE}/workspaces/users/current-user`;
const WORKSPACES_INVITED_URL = `${CNS_API_BASE}/workspaces/invited`;
const PLANS_URL = `${CNS_API_BASE}/plans`;

/**
 * Fetch workspaces for the current user (canonical endpoint).
 */
export async function fetchWorkspacesForCurrentUser(): Promise<CNSWorkspace[]> {
  const res = await fetch(WORKSPACES_CURRENT_USER_URL, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch workspaces");
  const data: CNSWorkspace[] = await res.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Fetch workspaces for the current user (uses GET /workspaces for backward compatibility).
 * Prefer fetchWorkspacesForCurrentUser() for the canonical "my workspaces" URL.
 */
export async function fetchWorkspaces(): Promise<CNSWorkspace[]> {
  return fetchWorkspacesForCurrentUser();
}

/**
 * Fetch workspaces the current user is invited to (member, not owner). Each item is normalized with isShared: true.
 */
export async function fetchInvitedWorkspaces(): Promise<CNSWorkspace[]> {
  const res = await fetch(WORKSPACES_INVITED_URL, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch invited workspaces");
  const data: CNSWorkspace[] = await res.json();
  return Array.isArray(data) ? data.map((w) => ({ ...w, isShared: true as const })) : [];
}

/**
 * Get a single workspace by ID.
 */
export async function getWorkspace(workspaceId: string): Promise<CNSWorkspace> {
  const res = await fetch(`${WORKSPACES_URL}/${workspaceId}`, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch workspace");
  return res.json();
}

/**
 * Workspace analytics (counts + median learning cycle). Sends browser IANA zone for median calendar boundaries.
 */
export async function fetchWorkspaceAnalytics(workspaceId: string): Promise<CNSWorkspaceAnalytics> {
  const params = new URLSearchParams({ timezone: getBrowserIanaTimeZone() });
  const res = await fetch(`${WORKSPACES_URL}/${workspaceId}/analytics?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch workspace analytics");
  return res.json();
}

/**
 * Create a new workspace. Backend creates group and adds owner as first member.
 */
export async function createWorkspace(
  name: string,
  type?: CNSCreateWorkspace["type"]
): Promise<CNSWorkspace> {
  const body: CNSCreateWorkspace = { name };
  if (type) body.type = type;
  const res = await fetch(WORKSPACES_URL, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to create workspace");
  return res.json();
}

/**
 * List plan templates available for onboarding.
 */
export async function fetchPlanTemplates(): Promise<CNSPlanTemplateSummary[]> {
  const res = await fetch(PLANS_URL, { credentials: "include" });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch plan templates");
  const data: CNSPlanTemplateSummary[] = await res.json();
  return Array.isArray(data) ? data : [];
}

/**
 * Create billing profile/account for first-time workspace plan selection.
 */
export async function createWorkspaceBillingProfile(
  workspaceId: string,
  planTemplatePublicId: string
): Promise<CNSWorkspaceBillingProfileAssignment> {
  const res = await fetch(`${WORKSPACES_URL}/${workspaceId}/billing-profile`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ planTemplatePublicId }),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to create workspace billing profile");
  return res.json();
}

/**
 * Update billing profile plan template for an existing workspace.
 */
export async function updateWorkspaceBillingProfile(
  workspaceId: string,
  planTemplatePublicId: string
): Promise<CNSWorkspaceBillingProfileAssignment> {
  const res = await fetch(`${WORKSPACES_URL}/${workspaceId}/billing-profile`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json-patch+json" },
    body: JSON.stringify([
      { op: "replace", path: "/planTemplatePublicId", value: planTemplatePublicId },
    ]),
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to update workspace billing profile");
  return res.json();
}

/**
 * Read billing profile assignment for an existing workspace.
 */
export async function fetchWorkspaceBillingProfile(
  workspaceId: string
): Promise<CNSWorkspaceBillingProfileAssignment> {
  const res = await fetch(`${WORKSPACES_URL}/${workspaceId}/billing-profile`, {
    credentials: "include",
  });
  if (!res.ok) await throwOnProblemResponse(res, "Failed to fetch workspace billing profile");
  return res.json();
}
